import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { ResourceUtils } from 'app/utils/resource.utils';

/**
 * Size variants available for responsive images
 */
export enum ImageSize {
  THUMB = 'thumb',   // Small thumbnail (200px width)
  MEDIUM = 'medium', // Medium size (800px width)
  LARGE = 'large'    // Large size (1280px width)
}

/**
 * Service for handling responsive image URLs and srcset generation
 * Updated to work with the optimized backend image processing service and CDN support
 */
@Injectable({
  providedIn: 'root'
})
export class ResponsiveImageService {
  // Preferred image format
  private readonly FORMAT_EXTENSION = 'webp';
  // Fallback format used by the server when WebP is not supported
  private readonly FALLBACK_FORMAT = 'jpg';
  
  // Available sizes for the current implementation
  private readonly AVAILABLE_SIZES = [ImageSize.MEDIUM, ImageSize.LARGE];
  
  constructor() { }
  
  /**
   * Constructs a URL for a specific image size variant
   * @param basePath The base image path from the database with extension
   * @param size The desired image size variant
   * @param planCode The plan code to determine CDN vs bucket usage
   * @returns The complete URL to the image
   */
  getImageUrl(basePath: string, size: ImageSize, planCode?: string): string {
    if (!basePath) {
      return '';
    }
    
    // If requested size is MEDIUM but not available, use LARGE instead
    const targetSize = !this.AVAILABLE_SIZES.includes(size) ? ImageSize.LARGE : size;
    
    // Extract the directory and base filename without extension
    const lastSlashIndex = basePath.lastIndexOf('/');
    const pathPart = lastSlashIndex >= 0 ? basePath.substring(0, lastSlashIndex + 1) : '';
    const filenamePart = lastSlashIndex >= 0 ? basePath.substring(lastSlashIndex + 1) : basePath;
    
    // Extract the base name without extension
    const lastDotIndex = filenamePart.lastIndexOf('.');
    const baseFilename = lastDotIndex >= 0 ? filenamePart.substring(0, lastDotIndex) : filenamePart;

    // Determine the base URL (CDN or bucket) based on plan code
    const baseUrl = planCode ? ResourceUtils.getResourceBaseUrl(planCode) : environment.bucket;
    
    // Construct the URL for the specific size with WebP format
    return `${baseUrl}${pathPart}${baseFilename}_${targetSize}.${this.FORMAT_EXTENSION}`;
  }
  
  /**
   * Constructs a fallback URL for browsers that don't support WebP
   * Uses JPEG format as the fallback format
   * @param basePath The base image path from the database
   * @param size The desired image size variant
   * @param planCode The plan code to determine CDN vs bucket usage
   * @returns The URL to the fallback image
   */
  getFallbackImageUrl(basePath: string, size: ImageSize = ImageSize.LARGE, planCode?: string): string {
    if (!basePath) {
      return '';
    }
    
    // If requested size is MEDIUM but not available, use LARGE instead
    const targetSize = !this.AVAILABLE_SIZES.includes(size) ? ImageSize.LARGE : size;
    
    // Extract the directory and base filename without extension
    const lastSlashIndex = basePath.lastIndexOf('/');
    const pathPart = lastSlashIndex >= 0 ? basePath.substring(0, lastSlashIndex + 1) : '';
    const filenamePart = lastSlashIndex >= 0 ? basePath.substring(lastSlashIndex + 1) : basePath;
    
    // Extract the base name without extension
    const lastDotIndex = filenamePart.lastIndexOf('.');
    const baseFilename = lastDotIndex >= 0 ? filenamePart.substring(0, lastDotIndex) : filenamePart;
    
    // Determine the base URL (CDN or bucket) based on plan code
    const baseUrl = planCode ? ResourceUtils.getResourceBaseUrl(planCode) : environment.bucket;
    
    // Construct the URL for the specified size with the fallback format (JPEG)
    return `${baseUrl}${pathPart}${baseFilename}_${targetSize}.${this.FALLBACK_FORMAT}`;
  }
  
  /**
   * Generates a set of sources for the different available image sizes
   * @param basePath Base path of the image
   * @param planCode The plan code to determine CDN vs bucket usage
   * @returns A string formatted for the img srcset attribute
   */
  generateSrcSet(basePath: string, planCode?: string): string {
    if (!basePath) {
      return '';
    }
    // Generate srcset for different image sizes
    return `
      ${this.getImageUrl(basePath, ImageSize.MEDIUM, planCode)} 800w,
      ${this.getImageUrl(basePath, ImageSize.LARGE, planCode)} 1280w
    `.trim();
  }
  
  /**
   * Generates appropriate sizes attribute for responsive images
   * Updated to use MEDIUM and LARGE sizes
   * @returns A string containing the sizes attribute value
   */
  generateSizes(): string {
    return '(max-width: 600px) 800px, 1280px';
  }
  
  /**
   * Check if the browser supports WebP format
   * This could be used to determine whether to use <picture> element or not
   * @returns Promise that resolves to true if WebP is supported, false otherwise
   */
  async isWebPSupported(): Promise<boolean> {
    return new Promise((resolve) => {
      const webpImg = new Image();
      webpImg.onload = () => resolve(true);
      webpImg.onerror = () => resolve(false);
      webpImg.src = 'data:image/webp;base64,UklGRh4AAABXRUJQVlA4TBEAAAAvAAAAAAfQ//73v/+BiOh/AAA=';
    });
  }
} 